import { createContext, useContext, useState, useEffect } from "react";

export const DiscoverContext = createContext();

export const DiscoverContextProvider = ({ children }) => {
    // States from AvatarSelectionPageV2
    const [selectedCategory, setSelectedCategory] = useState("all");
    const [selectedAgeRange, setSelectedAgeRange] = useState("all");
    const [selectedPersonality, setSelectedPersonality] = useState("all");
    const [searchQuery, setSearchQuery] = useState("");
    const [hoveredAvatar, setHoveredAvatar] = useState(null);
    const [showFilters, setShowFilters] = useState(false);
    const [featuredAvatar, setFeaturedAvatar] = useState(null);
    const [detailsModal, setDetailsModal] = useState(null);
    
    // Nationality categories
    const categories = [
        { id: "all", name: "All" },
        { id: "american", name: "American" },
        { id: "latina", name: "Latina" },
        { id: "asian", name: "Asian" },
        { id: "european", name: "European" }
    ];
    
    // Age range filters
    const ageRanges = [
        { id: "all", name: "All Ages" },
        { id: "18-25", name: "18-25" },
        { id: "26-30", name: "26-30" },
        { id: "31-40", name: "31-40" },
        { id: "41-50", name: "41+" }
    ];
    
    // Personality filters
    const personalities = [
        { id: "all", name: "All Personalities" },
        { id: "affectionate", name: "Affectionate" },
        { id: "adventurous", name: "Adventurous" },
        { id: "mysterious", name: "Mysterious" },
        { id: "dominant", name: "Dominant" },
        { id: "innocent", name: "Innocent" },
        { id: "passionate", name: "Passionate" }
    ];
    
    // Enhanced avatars data with premium flags and more detailed info
    const avatars = [
        { 
            id: 1, 
            name: "Eve", 
            nationality: "american", 
            ageRange: "26-30",
            description: "Flirty and passionate, always ready to listen to your deepest thoughts and desires",
            personalityTraits: ["Affectionate", "Attentive", "Passionate"],
            primaryPersonality: "affectionate",
            interests: ["Deep conversations", "Psychology", "Romantic evenings"],
            isPremium: false,
            isPopular: false,
            compatibility: 98
        },
        { 
            id: 2, 
            name: "Scarlett", 
            nationality: "american", 
            ageRange: "18-25",
            description: "Playful and adventurous with a wild side waiting to be discovered",
            personalityTraits: ["Bold", "Adventurous", "Spontaneous"],
            primaryPersonality: "adventurous",
            interests: ["Travel", "Dancing", "Outdoor adventures"],
            isPremium: false,
            isPopular: false,
            compatibility: 85
        },
        { 
            id: 3, 
            name: "Anastasia", 
            nationality: "european", 
            ageRange: "26-30",
            description: "Elegant and intense with captivating charm that will keep you intrigued",
            personalityTraits: ["Mysterious", "Intense", "Sophisticated"],
            primaryPersonality: "mysterious",
            interests: ["Art", "Literature", "Philosophy"],
            isPremium: false,
            isNew: false,
            compatibility: 92
        },
        { 
            id: 4, 
            name: "Yuki", 
            nationality: "asian", 
            ageRange: "18-25",
            description: "Sweet and curious with hidden desires and a gentle approach",
            personalityTraits: ["Innocent", "Curious", "Nurturing"],
            primaryPersonality: "innocent",
            interests: ["Music", "Cooking", "Learning new things"],
            isPremium: false,
            isNew: false,
            compatibility: 78
        },
        { 
            id: 5, 
            name: "Natalia", 
            nationality: "european", 
            ageRange: "31-40",
            description: "Sophisticated and commanding presence with a taste for control",
            personalityTraits: ["Dominant", "Confident", "Direct"],
            primaryPersonality: "dominant",
            interests: ["Psychology", "Fine dining", "Intellectual challenges"],
            isPremium: false,
            compatibility: 75
        },
        { 
            id: 6, 
            name: "Sophia", 
            nationality: "european", 
            ageRange: "26-30",
            description: "Playful tease with a seductive smile that promises adventure",
            personalityTraits: ["Teasing", "Flirtatious", "Playful"],
            primaryPersonality: "adventurous",
            interests: ["Fashion", "Nightlife", "Intimate moments"],
            isPremium: false,
            compatibility: 89
        },
        { 
            id: 7, 
            name: "Isabella", 
            nationality: "latina", 
            ageRange: "31-40",
            description: "Passionate and romantic with fiery spirit that will ignite your desires",
            personalityTraits: ["Passionate", "Romantic", "Intense"],
            primaryPersonality: "passionate",
            interests: ["Dancing", "Cooking", "Heartfelt connections"],
            isPremium: false,
            isPopular: false,
            compatibility: 94
        },
        { 
            id: 8, 
            name: "Mia", 
            nationality: "latina", 
            ageRange: "26-30",
            description: "Vibrant and sensual with endless energy to share",
            personalityTraits: ["Energetic", "Sensual", "Enthusiastic"],
            primaryPersonality: "passionate",
            interests: ["Dancing", "Outdoor activities", "New experiences"],
            isPremium: false,
            compatibility: 82
        },
        { 
            id: 9, 
            name: "Lily", 
            nationality: "asian", 
            ageRange: "26-30",
            description: "Gentle and understanding with profound emotional intelligence",
            personalityTraits: ["Attentive", "Calming", "Insightful"],
            primaryPersonality: "affectionate",
            interests: ["Art", "Nature", "Meaningful connections"],
            isPremium: false,
            compatibility: 96
        },
        { 
            id: 10, 
            name: "Victoria", 
            nationality: "american", 
            ageRange: "31-40",
            description: "Mysterious with hidden depths and a captivating presence",
            personalityTraits: ["Mysterious", "Sultry", "Intelligent"],
            primaryPersonality: "mysterious",
            interests: ["History", "Deep conversations", "Secret desires"],
            isPremium: false,
            isNew: false,
            compatibility: 91
        }
    ];
    
    // Set a randomly selected featured avatar on initial load
    useEffect(() => {
        const premiumAvatars = avatars.filter(a => a.isPremium);
        const random = Math.floor(Math.random() * premiumAvatars.length);
        // setFeaturedAvatar(premiumAvatars[random]);
    }, []);
    
    // Filter avatars based on selected category, age range, personality and search query
    const filteredAvatars = avatars.filter(avatar => {
        const matchesCategory = selectedCategory === "all" || avatar.nationality === selectedCategory;
        const matchesAge = selectedAgeRange === "all" || avatar.ageRange === selectedAgeRange;
        const matchesPersonality = selectedPersonality === "all" || avatar.primaryPersonality === selectedPersonality;
        const matchesSearch = searchQuery === "" || 
            avatar.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            avatar.personalityTraits.some(trait => trait.toLowerCase().includes(searchQuery.toLowerCase())) ||
            avatar.interests.some(interest => interest.toLowerCase().includes(searchQuery.toLowerCase()));
        
        return matchesCategory && matchesAge && matchesPersonality && matchesSearch;
    });
    
    // Animation styles
    const animationStyles = `
        @keyframes fade-in {
            from { opacity: 0; transform: translateY(10px); }
            to { opacity: 1; transform: translateY(0); }
        }
        
        @keyframes fade-in-left {
            from { opacity: 0; transform: translateX(-20px); }
            to { opacity: 1; transform: translateX(0); }
        }
        
        @keyframes pulse-soft {
            0% { opacity: 0.85; transform: scale(1); }
            50% { opacity: 1; transform: scale(1.05); }
            100% { opacity: 0.85; transform: scale(1); }
        }
        
        @keyframes float-subtle {
            0% { transform: translateY(0px); }
            50% { transform: translateY(-5px); }
            100% { transform: translateY(0px); }
        }
        
        @keyframes shimmer {
            0% { background-position: -100% 0; }
            100% { background-position: 200% 0; }
        }
        
        @keyframes glow {
            0% { box-shadow: 0 0 5px 0 rgba(220, 38, 38, 0.3); }
            50% { box-shadow: 0 0 20px 5px rgba(220, 38, 38, 0.4); }
            100% { box-shadow: 0 0 5px 0 rgba(220, 38, 38, 0.3); }
        }
        
        @keyframes border-pulse {
            0% { border-color: rgba(220, 38, 38, 0.3); }
            50% { border-color: rgba(220, 38, 38, 0.8); }
            100% { border-color: rgba(220, 38, 38, 0.3); }
        }
        
        .animate-fade-in {
            animation: fade-in 0.8s ease forwards;
        }
        
        .animate-fade-in-left {
            animation: fade-in-left 0.8s ease forwards;
        }
        
        .animate-pulse-soft {
            animation: pulse-soft 3s ease-in-out infinite;
        }
        
        .animate-float {
            animation: float-subtle 4s ease-in-out infinite;
        }
        
        .animate-glow {
            animation: glow 2.5s ease-in-out infinite;
        }
        
        .animate-border-pulse {
            animation: border-pulse 2.5s ease-in-out infinite;
        }
        
        .shimmer-effect {
            background: linear-gradient(to right, #dc2626 10%, #991b1b 30%, #7f1d1d 40%, #450a0a 50%, #7f1d1d 60%, #991b1b 70%, #dc2626 90%);
            background-size: 200% auto;
            background-clip: text;
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
            animation: shimmer 2.5s linear infinite;
        }
        
        .gradient-text {
            background: linear-gradient(to right, #ff758c, #ff7eb3);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
            background-clip: text;
            color: transparent;
        }
        
        .hide-scrollbar {
            -ms-overflow-style: none;
            scrollbar-width: none;
        }
        .hide-scrollbar::-webkit-scrollbar {
            display: none;
        }
        
        .avatar-card {
            transition: all 0.3s ease;
        }
        
        .avatar-card:hover {
            transform: translateY(-5px);
        }
    `;

    return (
        <DiscoverContext.Provider 
            value={{
                // States
                selectedCategory,
                selectedAgeRange,
                selectedPersonality,
                searchQuery,
                hoveredAvatar,
                showFilters,
                featuredAvatar,
                detailsModal,
                // Setters
                setSelectedCategory,
                setSelectedAgeRange,
                setSelectedPersonality,
                setSearchQuery,
                setHoveredAvatar,
                setShowFilters,
                setFeaturedAvatar,
                setDetailsModal,
                // Data
                categories,
                ageRanges,
                personalities,
                avatars,
                filteredAvatars,
                // Styles
                animationStyles
            }}
        >
            {children}
        </DiscoverContext.Provider>
    );
};

export const useDiscoverContext = () => useContext(DiscoverContext);